<i18n>
[
    "product__size--label",
    "product__noSizeSelected--label",
    "product__skuSoldOutColor--message",
    "product__allSoldOut--message",
    "product__skuSoldOut--message",
]
</i18n>

<template>
    <div>
        <div class="u-spacer--1pt5 o-text--caption">
            <span>
                {{ $t("product__size--label") }}
                <span
                    v-if="selectedSku"
                    class="c-product-size-selector__size-text"
                >
                    {{ sizeDisplayName }}
                </span>
                <span v-else class="u-text--gray">
                    {{ $t("product__noSizeSelected--label") }}
                </span>
            </span>
        </div>
        <SkuSelector
            :key="componentKey"
            :skuSelectorId="'pdp-sku-selector'"
            :skuSelectorName="'pdp-sku-selector'"
            :sizeGroups="selectedChoice.sizeGroups"
            :selectedColorCode="colorCode"
            :initialSizeType="sizeType"
            :initialSize="size"
            :initialSku="selectedSku"
            :purchasableSkus="purchasableSkus"
            :emitClickEvent="true"
            @update-sku="skuUpdated"
            @click="sizeClick"
        />
        <p
            v-if="noInventory && (!hasError || addToBoxError)"
            class="c-product-size-selector__sold-out-text o-text--caption u-spacer--2"
        >
            {{ $t("product__allSoldOut--message") }}
        </p>
        <BaseSupportingTextError
            v-if="hasError && !addToBoxError"
            :errorText="soldOutMessage"
        />
    </div>
</template>

<script setup>
// NU_FEATURE: USE_BOX_COLOR_SELECTOR
// https://urbnit.atlassian.net/browse/TYP-27174
// delete this component
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useState, useGetters, useMutations, useActions } from '~coreModules/core/js/composables/vuexHelpers';

import {
    SET_SKU_CODE,
    SET_SKU_SIZE_CODE,
    SET_ADD_TO_BOX_ERROR,
    UPDATE_ITEM_IN_BOX_STATUS,
} from '~routes/product/js/product-store';
import { MODALS_MODULE_NAME, POP_MODALS } from '~coreModules/modals/js/modals-store';

import { getProductModuleNameFromRoute } from '~coreRoutes/product/js/product-utils';

import SkuSelector from '~modules/core/components/ui/SkuSelector.vue';
import BaseSupportingTextError from '~coreModules/core/components/ui/form-elements/BaseSupportingTextError.vue';

const props = defineProps({
    selectedChoice: {
        type: Object,
        required: true,
    },
    colorCode: {
        type: String,
        required: true,
    },
    sizeCodeAvailibilityByChoiceId: {
        type: Object,
        required: true,
    },
});

const route = useRoute();
const { t } = useI18n();

// UX is asking for click on disabled skuSelector to rerender
// there is currently an issue where updating the values will not force the rerender
// this is a stopgap to force a rerender by incrementing the value when we show the error
const componentKey = ref(0);
const sizeType = ref('');
const size = ref('');
const sizeDisplayName = ref('');
const soldOutMessage = ref('');
const hasError = ref(false);

const productModuleName = computed(() => getProductModuleNameFromRoute(route));
const {
    selectedSku,
    selectedSkuSizeCode,
    addToBoxError,
} = useState(productModuleName.value, {
    selectedSku: state => state.selectedSku,
    selectedSkuSizeCode: state => state.selectedSkuSizeCode,
    addToBoxError: state => state.addToBoxError,
});

const { popModals } = useActions(MODALS_MODULE_NAME, { popModals: POP_MODALS });

const {
    setSkuCode,
    setSkuSizeCode,
    setErrorText,
    updateItemInBoxStatus,
} = useMutations(productModuleName.value, {
    setSkuCode: SET_SKU_CODE,
    setSkuSizeCode: SET_SKU_SIZE_CODE,
    setErrorText: SET_ADD_TO_BOX_ERROR,
    updateItemInBoxStatus: UPDATE_ITEM_IN_BOX_STATUS,
});

const { purchasableSkus } = useGetters(productModuleName.value, ['purchasableSkus']);

const noInventory = computed(() => {
    const sizeGroups = props.selectedChoice?.sizeGroups ?? [];
    const hasInventory = sizeGroups.find(({ includedSkus }) => includedSkus.find(sku => sku.availableInventory > 0));
    return !hasInventory;
});

const isSelectedSkuSizeCodeAvailableInSelectedChoice = computed(() => (
    props.sizeCodeAvailibilityByChoiceId[props.selectedChoice.choiceId]
        ?.includes(selectedSkuSizeCode.value?.code)
));

function skuUpdated(sku) {
    setSkuCode(sku?.skuId || '');
    setSkuSizeCode(sku?.size || selectedSkuSizeCode.value);
    updateItemInBoxStatus(false);
    setErrorText('');
}

function sizeClick(event) {
    if (event.disabled && noInventory.value) {
        soldOutMessage.value = t('product__allSoldOut--message');
        hasError.value = true;
    } else if (event.disabled && !noInventory.value) {
        setSkuCode('');
        setSkuSizeCode('');
        componentKey.value += 1;
        size.value = '';
        sizeType.value = '';
        soldOutMessage.value = t(
            'product__skuSoldOutColor--message', [event.label, props.selectedChoice?.color?.displayName],
        );
        hasError.value = true;
    } else {
        sizeDisplayName.value = event.label;
        hasError.value = false;
        soldOutMessage.value = '';
    }
}

function handleSkuPreselection() {
    let sku = route?.query?.sku;

    if (!sku) {
        if (purchasableSkus.value?.length) {
            sku = purchasableSkus.value?.[0];
        } else {
            return;
        }
    }

    let skuObject;

    /* eslint-disable-next-line arrow-body-style */
    props.selectedChoice.sizeGroups.find((sizeGroup) => {
        return sizeGroup.includedSkus.find((includedSku) => {
            if (includedSku.skuId === sku && (includedSku.availableInventory > 0 ||
                purchasableSkus.value.includes(sku))) {
                size.value = includedSku?.size?.code ?? '';
                sizeDisplayName.value = includedSku?.size?.displayName ?? '';
                sizeType.value = sizeGroup?.groupCode ?? '';
                skuObject = includedSku;
                return true;
            }

            return false;
        });
    });

    skuUpdated(skuObject);
}

function setCommonSelectorProperties({ skuId, sizeDisplayName: newSizeDisplayName, hasError: newHasError }) {
    setSkuCode(skuId);
    sizeDisplayName.value = newSizeDisplayName;
    hasError.value = newHasError;
}

function findAvailableSkuForChoiceId() {
    let foundSkuData = {};

    props.selectedChoice.sizeGroups.find(sizeGroup => sizeGroup.includedSkus.find((includedSku) => {
        if (includedSku?.size?.code === selectedSkuSizeCode.value?.code) {
            foundSkuData = {
                ...includedSku,
                sizeType: sizeGroup?.groupCode || '',
            };
            return true;
        }
        return false;
    }));

    return foundSkuData;
}

watch(() => props.colorCode, (newColor, oldColor) => {
    if (newColor === oldColor) return;

    if (selectedSkuSizeCode.value) {
        if (isSelectedSkuSizeCodeAvailableInSelectedChoice.value) {
            const skuData = findAvailableSkuForChoiceId();

            setCommonSelectorProperties({
                skuId: skuData?.skuId,
                sizeDisplayName: skuData?.size?.displayName,
                hasError: false,
            });
            size.value = skuData?.size?.code;
            sizeType.value = skuData?.sizeType;
        } else {
            setCommonSelectorProperties({
                skuId: '',
                sizeDisplayName: '',
                hasError: true,

            });
            size.value = '';
            sizeType.value = props.selectedChoice.sizeGroups?.[0]?.groupCode;
            soldOutMessage.value = t(
                'product__skuSoldOutColor--message',
                [selectedSkuSizeCode.value?.displayName,
                    props.selectedChoice?.color?.displayName],
            );
        }
    }
});

watch(() => route.query, (newQuery, oldQuery) => {
    // if there is both a previous and next value, we need to pop the modals here
    // this means that the user clicked another box tile for a different size
    if (newQuery?.sku !== oldQuery?.sku) {
        popModals();
        handleSkuPreselection();
    }
});

onMounted(() => {
    handleSkuPreselection();
});

</script>
<style lang="scss">
    .c-product-size-selector {
        &__size-text,
        &__sold-out-text {
            color: $nu-gray;
        }
    }
</style>
